import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Calendar, 
  DollarSign, 
  Dog, 
  TrendingUp,
  BarChart,
  Clock,
  Download,
  Eye
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';

interface MonthlyReport {
  _id: string;
  branchId: string;
  branchName: string;
  period: {
    startDate: string;
    endDate: string;
    year: number;
    month: number;
    description?: string;
  };
  stats: {
    totalDogs: number;
    totalDays: number;
    totalAmount: number;
    daycareRate: number;
    hotelRate: number;
  };
  visits: Array<{
    dogName: string;
    ownerName: string;
    checkInDate: string;
    checkOutDate: string;
    days: number;
    amount: number;
  }>;
  status: 'active' | 'completed';
  paymentStatus?: 'pending' | 'paid' | 'overdue';
  paymentDueDate?: string;
  completedAt?: string;
  createdAt: string;
}

const AylikOdemeRaporu: React.FC = () => {
  const [currentReport, setCurrentReport] = useState<MonthlyReport | null>(null);
  const [pastReports, setPastReports] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<MonthlyReport | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [branchName, setBranchName] = useState('');

  const [daycareRate, setDaycareRate] = useState<number>(69); // Kreş ücreti (30dk-13 saat)
  const [hotelRate, setHotelRate] = useState<number>(99); // Otel ücreti (30dk-36 saat)
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.REPORTS_MONTHLY_PAYMENT}`,
        {
          method: 'GET',
          headers,
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setCurrentReport(data.data.currentReport || null);
        setPastReports(data.data.pastReports || []);
        setBranchName(data.data.branchName || 'Oteliniz');
        setDaycareRate(parseInt(data.data.daycareRate) || 69); // Database'den kreş ücreti çek
        setHotelRate(parseInt(data.data.hotelRate) || 99); // Database'den otel ücreti çek
      } else {
        throw new Error(data.message || 'Raporlar getirilemedi');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const formatDuration = useCallback((checkInDate: string, checkOutDate: string) => {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const diffMs = checkOut.getTime() - checkIn.getTime();
    
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;
    
    let result = '';
    
    if (days > 0) {
      result += `${days}g`;
    }
    if (hours > 0) {
      if (result) result += ' ';
      result += `${hours}sa`;
    }
    if (minutes > 0) {
      if (result) result += ' ';
      result += `${minutes}dk`;
    }
    
    // Eğer hiçbir şey yoksa (çok kısa süre)
    if (!result) {
      result = '0dk';
    }
    
    return result;
  }, []);

  const formatCurrency = useCallback((amount: number) => {
    // NaN veya geçersiz değerleri kontrol et
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '₺0';
    }
    return `₺${new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0
    }).format(amount)}`;
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }, []);

  const getMonthName = useCallback((month: number) => {
    const months = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];
    return months[month - 1];
  }, []);

  const getCurrentPeriodInfo = useCallback(() => {
    // Dinamik dönem hesaplama - her ayın 8'i ile 7'si arası
    const now = new Date();
    const currentDay = now.getDate();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    let startDate, endDate;
    
    if (currentDay >= 8) {
      // Bu ayın 8'inden sonraysak, bu ay başlangıç
      startDate = new Date(currentYear, currentMonth, 8);
      endDate = new Date(currentYear, currentMonth + 1, 7);
    } else {
      // Bu ayın 8'inden önceyse, geçen ay başlangıç
      startDate = new Date(currentYear, currentMonth - 1, 8);
      endDate = new Date(currentYear, currentMonth, 7);
    }
    
    return { startDate, endDate };
  }, []);

  const downloadHistoricalExcelReport = useCallback(async (report: MonthlyReport) => {
    try {
      setDownloadingPdf(true);
      
      // CSV formatında oluştur - daha basit ve uyumlu!
      let csvContent = '';
      
      // Başlık verileri
      csvContent += 'AYLIK ÖDEME RAPORU\n';
      csvContent += `Şube: ${report.branchName}\n`;
      csvContent += `Dönem: ${report.period.month}/${report.period.year}\n`;
      csvContent += '\n';
      csvContent += 'ÖZET\n';
      csvContent += `Kedi/Köpek Sayısı,${report.stats.totalDogs}\n`;
      csvContent += `Toplam Gün,${report.stats.totalDays}\n`;
      csvContent += `Kreş Fiyatı,${report.stats.daycareRate || 0}\n`;
      csvContent += `Otel Fiyatı,${report.stats.hotelRate || 0}\n`;
      csvContent += `Toplam Tutar,${report.stats.totalAmount}\n`;
      csvContent += '\n';
      csvContent += 'ZİYARET DETAYLARI\n';
      csvContent += 'Köpek Adı,Sahip,Giriş,Çıkış,Süre,Tutar\n';
      
      // Detaylı kayıtları ekle
      report.visits.forEach(visit => {
        csvContent += `${visit.dogName},${visit.ownerName},${visit.checkInDate},${visit.checkOutDate},${visit.days},${visit.amount}\n`;
      });
      
      // Dosya adını oluştur
      const fileName = `aylik-odeme-raporu-${report.period.year}-${String(report.period.month).padStart(2, '0')}.csv`;
      
      // Dosyayı indir
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Historical CSV indirme hatası:', error);
      setError(error instanceof Error ? error.message : 'CSV indirilemedi');
    } finally {
      setDownloadingPdf(false);
    }
  }, []);

  const handleViewDetails = useCallback((report: MonthlyReport) => {
    setSelectedReport(report);
    setShowDetails(true);
  }, []);

  const handleDownloadReport = useCallback((report: MonthlyReport) => {
    downloadHistoricalExcelReport(report);
  }, [downloadHistoricalExcelReport]);

  const downloadExcelReport = useCallback(async () => {
    try {
      setDownloadingPdf(true);
      
      // Mevcut raporu kullan - backend'e gitmeden!
      const reportToExport = selectedReport || currentReport;
      
      if (!reportToExport) {
        throw new Error('İndirilecek rapor bulunamadı');
      }

      // CSV formatında oluştur - daha basit ve uyumlu!
      let csvContent = '';
      
      // Başlık verileri
      csvContent += 'AYLIK ÖDEME RAPORU\n';
      csvContent += `Şube: ${reportToExport.branchName}\n`;
      csvContent += `Dönem: ${reportToExport.period.month}/${reportToExport.period.year}\n`;
      csvContent += '\n';
      csvContent += 'ÖZET\n';
      csvContent += `Kedi/Köpek Sayısı,${reportToExport.stats.totalDogs}\n`;
      csvContent += `Toplam Gün,${reportToExport.stats.totalDays}\n`;
      csvContent += `Kreş Fiyatı,${reportToExport.stats.daycareRate || 0}\n`;
      csvContent += `Otel Fiyatı,${reportToExport.stats.hotelRate || 0}\n`;
      csvContent += `Toplam Tutar,${reportToExport.stats.totalAmount}\n`;
      csvContent += '\n';
      csvContent += 'ZİYARET DETAYLARI\n';
      csvContent += 'Köpek Adı,Sahip,Giriş,Çıkış,Süre,Tutar\n';
      
      // Detaylı kayıtları ekle
      reportToExport.visits.forEach(visit => {
        csvContent += `${visit.dogName},${visit.ownerName},${visit.checkInDate},${visit.checkOutDate},${visit.days},${visit.amount}\n`;
      });
      
      // Dosya adını oluştur
      const fileName = `aylik-odeme-raporu-${reportToExport.period.year}-${String(reportToExport.period.month).padStart(2, '0')}.csv`;
      
      // Dosyayı indir
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('CSV indirme hatası:', error);
      setError(error instanceof Error ? error.message : 'CSV indirilemedi');
    } finally {
      setDownloadingPdf(false);
    }
  }, [selectedReport, currentReport]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Raporlar yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-red-800 font-semibold">Hata Oluştu</h3>
            <p className="text-red-600 mt-2">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const { startDate, endDate } = getCurrentPeriodInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-3 rounded-xl">
                <BarChart className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Aylık Ödeme Raporları</h1>
                <p className="text-gray-600">{branchName} - Gelir Takip Sistemi</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 bg-green-100 px-4 py-2 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-green-800">Kreş: {formatCurrency(daycareRate)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-green-800">Otel: {formatCurrency(hotelRate)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Current Month Report */}
        {currentReport ? (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <TrendingUp className="text-green-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Aktif Dönem</h2>
                  <p className="text-gray-600">
                    {formatDate(currentReport.period.startDate)} - {formatDate(currentReport.period.endDate)}
                  </p>
                </div>
              </div>
              <div className="bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                <span className="text-green-700 font-semibold">🟢 Canlı Hesaplama</span>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Toplam Kedi/Köpek</p>
                    <p className="text-2xl font-bold">{currentReport.stats.totalDogs}</p>
                  </div>
                  <Dog className="text-blue-200" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Toplam Gün</p>
                    <p className="text-2xl font-bold">{currentReport.stats.totalDays}</p>
                  </div>
                  <Clock className="text-purple-200" size={32} />
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Toplam Tutar</p>
                    <p className="text-2xl font-bold">{formatCurrency(currentReport.stats.totalAmount)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm">Ortalama/Kedi/Köpek</p>
                    <p className="text-2xl font-bold">
                      {currentReport.stats.totalDogs > 0 
                        ? formatCurrency(currentReport.stats.totalAmount / currentReport.stats.totalDogs)
                        : formatCurrency(0)
                      }
                    </p>
                  </div>
                  <Clock className="text-orange-200" size={32} />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 flex space-x-4">
              <button 
                onClick={() => handleViewDetails(currentReport)}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Eye size={20} />
                <span>Detayları Görüntüle</span>
              </button>
              <button 
                onClick={downloadExcelReport}
                disabled={downloadingPdf}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingPdf ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Excel Oluşturuluyor...</span>
                  </>
                ) : (
                  <>
                    <Download size={20} />
                    <span>Excel İndir</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="text-center">
              <div className="bg-gray-100 p-4 rounded-xl w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Download className="text-gray-500" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aktif Dönem</h3>
              <p className="text-gray-600 mb-4">
                {formatDate(startDate.toISOString())} - {formatDate(endDate.toISOString())} dönemi için henüz veri bulunmuyor.
              </p>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <p className="text-orange-800 text-sm">
                  💰 Bu dönem için ödeme bilgileri {formatDate(new Date(endDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString())} tarihinde hazır olacaktır.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Past Reports */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-gray-100 p-2 rounded-lg">
              <Calendar className="text-gray-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Geçmiş Dönem Raporları</h2>
              <p className="text-gray-600">Tamamlanmış aylık dönemler</p>
            </div>
          </div>

          {pastReports.length > 0 ? (
            <div className="space-y-4">
              {pastReports.map((report) => (
                <div key={report._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <Calendar className="text-gray-600" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {getMonthName(report.period.month)} {report.period.year}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {formatDate(report.period.startDate)} - {formatDate(report.period.endDate)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(report.stats.totalAmount)}</p>
                        <p className="text-gray-600 text-sm">{report.stats.totalDogs} Kedi/Köpek • {report.stats.totalDays} gün</p>
                        {/* Ödeme durumu badge */}
                        <div className="mt-1">
                          {report.paymentStatus === 'paid' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✅ Ödendi
                            </span>
                          )}
                          {report.paymentStatus === 'pending' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              ⏳ Ödeme Bekliyor
                            </span>
                          )}
                          {report.paymentStatus === 'overdue' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              ⚠️ Gecikmiş
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewDetails(report)}
                          className="flex items-center space-x-1 bg-green-100 text-green-700 px-3 py-2 rounded-lg hover:bg-green-200 transition-colors"
                        >
                          <Eye size={16} />
                          <span className="text-sm">Görüntüle</span>
                        </button>
                        <button 
                          onClick={() => handleDownloadReport(report)}
                          className="flex items-center space-x-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Download size={16} />
                          <span className="text-sm">İndir</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="bg-gray-100 p-4 rounded-xl w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Download className="text-gray-500" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz Geçmiş Rapor Yok</h3>
              <p className="text-gray-600">İlk aylık dönem tamamlandığında burada görünecektir.</p>
            </div>
          )}
        </div>

        {/* Report Details Modal */}
        {showDetails && selectedReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    {getMonthName(selectedReport.period.month)} {selectedReport.period.year} Raporu
                  </h2>
                  <button 
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-gray-600 mt-1">
                  {formatDate(selectedReport.period.startDate)} - {formatDate(selectedReport.period.endDate)}
                </p>
              </div>

              <div className="p-6">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-600 text-sm font-medium">Kedi/Köpek Sayısı</p>
                    <p className="text-2xl font-bold text-blue-900">{selectedReport.stats.totalDogs}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-purple-600 text-sm font-medium">Toplam Gün</p>
                    <p className="text-2xl font-bold text-purple-900">{selectedReport.stats.totalDays}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-green-600 text-sm font-medium">Kreş Fiyatı</p>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(selectedReport.stats.daycareRate || daycareRate)}</p>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <p className="text-indigo-600 text-sm font-medium">Otel Fiyatı</p>
                    <p className="text-2xl font-bold text-indigo-900">{formatCurrency(selectedReport.stats.hotelRate || hotelRate)}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-orange-600 text-sm font-medium">Toplam Tutar</p>
                    <p className="text-2xl font-bold text-orange-900">{formatCurrency(selectedReport.stats.totalAmount)}</p>
                  </div>
                </div>

                {/* Visits Details */}
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ziyaret Detayları</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Kedi/Köpek</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Sahip</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Giriş</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Çıkış</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Süre</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Tutar</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedReport.visits.map((visit, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{visit.dogName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{visit.ownerName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(visit.checkInDate)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(visit.checkOutDate)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{formatDuration(visit.checkInDate, visit.checkOutDate)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{formatCurrency(visit.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AylikOdemeRaporu;